import React, { FC, useEffect, useState } from 'react';

import { Box, Stack } from '@chakra-ui/react';
import { filter } from 'lodash';
import map from 'lodash/map';
import { useNavigate } from 'react-router-dom';

import { Select } from 'components/DataEntry';
import { IconTypes } from 'components/Icons';
import { DashboardWidget } from 'components/Visualization/CDashboard/Widget/DashboardWidget';
import {
  FunnelStep,
  VulnerabilityPrioritization,
} from 'services/Vulnerability/types';
import FunnelChart from 'components/Visualization/Charts/FunnelChart';
import { FunnelStepProps } from 'components/Visualization/Charts/FunnelChart/types';
import { renderNoEntitiesFoundWithIcon } from 'containers/Dashboard/utils';
import { VulnerabilityViewByTabs } from 'containers/Visibility/Vulnerabilities/types';

interface Props {
  vulnerabilityPrioritization: VulnerabilityPrioritization;
  labelSpacing?: number;
  isLoading?: boolean;
  setShow?: (show: string) => void;
  showDropdown?: boolean;
  activeStepIds?: string[];
  setActiveStepIds?: (value: string[]) => void;
  subHeader?: React.ReactNode;
  showHostTypeSummary?: boolean;
}

// Module-level default so its reference is stable; an inline default array was
// recreated every render and, with showDropdown, re-ran the dropdown effect
// endlessly ("Maximum update depth exceeded").
const DEFAULT_ACTIVE_STEP_IDS: string[] = [
  FunnelStep.VulnerablePackages,
  FunnelStep.InternetExposedPackages,
  FunnelStep.PackagesWithKnownExploits,
  FunnelStep.PatchablePackages,
  FunnelStep.HostsToPatch,
];

export const VulnerabilitiesPrioritizationComponent: FC<Props> = props => {
  const {
    vulnerabilityPrioritization,
    isLoading,
    setShow,
    showDropdown = false,
    activeStepIds = DEFAULT_ACTIVE_STEP_IDS,
    setActiveStepIds,
    subHeader,
    showHostTypeSummary = false,
  } = props;
  const navigate = useNavigate();
  const [dropDownItems, setDropDownItems] = useState<Record<string, any>[]>([]);
  const [activeSteps, setActiveSteps] = useState<FunnelStepProps[]>([]);
  const [activeDropdownItems, setActiveDropdownItems] = useState<
    Record<string, any>[]
  >([]);
  const path =
    '/visibility/vulnerability?show=Details&tab=' +
    VulnerabilityViewByTabs.Package;
  const hostTypeSummary = showHostTypeSummary
    ? ((vulnerabilityPrioritization as any).host_types ?? [])
        .map(
          item =>
            `${item.resource_type}: ${item.resource_count.toLocaleString()}`,
        )
        .join(', ')
    : '';
  const onClick = (e, params) => {
    //e.stopPropagation();
    setShow?.('Details');
    navigate(params);
  };
  const mapping = {
    [FunnelStep.VulnerablePackages]: {
      //id: 0,
      id: FunnelStep.VulnerablePackages,
      name: FunnelStep.VulnerablePackages,
      label: (
        <Stack spacing={0}>
          <Box>Vulnerable</Box>
          <Box>packages</Box>
        </Stack>
      ),
      key: 'vulnerable_packages',
      onClick: e => onClick(e, path),
      color: '#e0dffb',
    },
    [FunnelStep.InternetExposedPackages]: {
      //id: 1,
      id: FunnelStep.InternetExposedPackages,
      name: FunnelStep.InternetExposedPackages,
      label: (
        <Stack spacing={0}>
          <Box>Exposed</Box>
          <Box>packages</Box>
        </Stack>
      ),
      key: 'internet_exposed_resource_packages',
      onClick: e => onClick(e, path + '&isInternetExposed=True'),
      color: '#f9d29b',
    },
    [FunnelStep.PackagesWithKnownExploits]: {
      //id: 2,
      id: FunnelStep.PackagesWithKnownExploits,
      name: FunnelStep.PackagesWithKnownExploits,
      label: (
        <Stack spacing={0}>
          <Box>Exploitable</Box>
          <Box>Packages</Box>
        </Stack>
      ),
      key: 'internet_exposed_resource_packages_with_exploitable_cve',
      onClick: e =>
        onClick(e, path + '&isInternetExposed=True&isExploitable=True'),
      color: '#fff5aa',
    },
    [FunnelStep.PatchablePackages]: {
      //id: 3,
      id: FunnelStep.PatchablePackages,
      name: FunnelStep.PatchablePackages,
      label: (
        <Stack spacing={0}>
          <Box>Patchable</Box>
          <Box>packages</Box>
        </Stack>
      ),
      key: 'internet_exposed_resource_patchable_packages_with_exploitable_cve',
      onClick: e =>
        onClick(
          e,
          path + '&isInternetExposed=True&isExploitable=True&isPatchable=True',
        ),
      color: '#fdd9e8',
    },
    [FunnelStep.HostsToPatch]: {
      //id: 4,
      id: FunnelStep.HostsToPatch,
      name: FunnelStep.HostsToPatch,
      label: (
        <Stack spacing={0}>
          <Box>Hosts to</Box>
          <Box>patch</Box>
        </Stack>
      ),
      key:
        'internet_exposed_resources_having_patchable_packages_with_explitable_cve',
      title: hostTypeSummary
        ? `Hosts to patch by type — ${hostTypeSummary}`
        : 'Hosts to patch',
      color: '#aee3ff',
    },
  };

  useEffect(() => {
    setActiveSteps(
      map(activeStepIds, o => ({
        ...mapping[o],
        value: vulnerabilityPrioritization[mapping[o]?.key],
      })),
    );
  }, [vulnerabilityPrioritization]); //do not include activeStepIds and mapping, throwing error

  useEffect(() => {
    showDropdown &&
      setDropDownItems(
        map(mapping, o => ({ label: o.name, value: o.id, data: o })),
      );
  }, [showDropdown]); //do not include mapping

  useEffect(() => {
    showDropdown &&
      setActiveDropdownItems(
        filter(dropDownItems, o => activeStepIds?.includes(o.label)),
      );
  }, [activeStepIds, showDropdown, dropDownItems]);

  return (
    <DashboardWidget
      label="Vulnerability Prioritization"
      noData={!activeSteps?.[0]?.value}
      noDataMessage={renderNoEntitiesFoundWithIcon('No vulnerabilities found')}
      isLoading={isLoading}
      subHeader={subHeader}
      rightHeader={
        showDropdown && (
          <Box w={48}>
            <Select
              options={dropDownItems}
              isMulti
              onChange={selected =>
                setActiveStepIds?.(map(selected, o => o.label))
              }
              placeholder="Manage filters"
              alwaysShowPlaceholder
              isAFilter
              menuPortalTarget={document.body}
              value={activeDropdownItems}
              showIconInValueContainer
              showTotalSelected
            />
          </Box>
        )
      }
      content={
        <Box w="full" h="full" px={2}>
          <FunnelChart
            activeSteps={activeSteps}
            activeStepIds={activeStepIds}
            setActiveStepIds={setActiveStepIds}
            isLoading={isLoading}
            allowRemovingSteps={showDropdown}
          />
        </Box>
      }
      styles={{ contentWrapper: { pb: 0, px: 2 } }}
    />
  );
};
